// Create a sales order (BUILD_SPEC.md §5.7). Header fields follow
// `salesOrderCreateSchema` (packages/core/src/schemas/sales.ts) EXACTLY: site,
// customer, an optional reference, optional notes, and lines — there is no
// expected/required-date field on this document (verified against the schema
// and the `sales_orders` table — packages/db/src/schema/sales.ts carries only
// `createdAt`/`updatedAt` audit columns, no planning date), so none is offered
// here.
import { useMemo, useState } from 'react';
import { Alert, Button, Group, Paper, Stack, Text, Textarea, Title, Tooltip } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { IconAlertCircle } from '@tabler/icons-react';
import { ApiRequestError } from '../../api/client';
import { useCanSell } from '../../auth/permissions';
import { CustomerSelect } from './components/CustomerSelect';
import { SiteSelect } from './components/SiteSelect';
import {
  emptySalesOrderLineRow,
  salesOrderLinesValid,
  toSalesOrderLineInputs,
  SalesOrderLinesEditor,
  type SalesOrderLineFormRow,
} from './components/SalesOrderLinesEditor';
import { useAllProducts, useCreateSalesOrder, type SalesOrderCreateWireInput } from './api';

export function SalesOrderNew() {
  const navigate = useNavigate();
  // Client-side role gate mirroring every other posting screen in this feature
  // (e.g. CreditNoteNew) — defense-in-depth/UX only; the server's
  // `requireRole('SALES_ACCOUNTS', 'ADMIN')` on POST /sales/sales-orders
  // remains the source of truth.
  const canSell = useCanSell();
  const { data: products } = useAllProducts();
  const productsById = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, p])), [products]);

  const [siteId, setSiteId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<SalesOrderLineFormRow[]>([emptySalesOrderLineRow()]);

  const createSalesOrder = useCreateSalesOrder();

  const linesValid = salesOrderLinesValid(lines, productsById);
  const canSubmit = Boolean(siteId) && Boolean(customerId) && linesValid;

  async function handleSubmit() {
    const body: SalesOrderCreateWireInput = {
      siteId,
      customerId,
      reference: reference.trim() || null,
      notes: notes.trim() || null,
      lines: toSalesOrderLineInputs(lines),
    };
    const created = await createSalesOrder.mutateAsync(body);
    void navigate({ to: '/sales-invoicing/sales-orders/$salesOrderId', params: { salesOrderId: created.id } });
  }

  return (
    <Stack gap="lg" maw={900}>
      <Group justify="space-between">
        <Title order={2}>New sales order</Title>
        <Button component={Link} to="/sales-invoicing/sales-orders" variant="subtle">
          Back to register
        </Button>
      </Group>

      {createSalesOrder.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {createSalesOrder.error instanceof ApiRequestError ? createSalesOrder.error.message : 'Failed to create the sales order.'}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="md">
          <Group grow>
            <SiteSelect label="Site" value={siteId} onChange={setSiteId} required />
            <CustomerSelect label="Customer" value={customerId} onChange={(id) => setCustomerId(id)} required />
          </Group>
          <Group grow>
            <Textarea label="Reference (optional)" autosize minRows={1} value={reference} onChange={(e) => setReference(e.currentTarget.value)} />
            <Textarea label="Notes (optional)" autosize minRows={1} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Lines
          </Text>
          <SalesOrderLinesEditor rows={lines} onChange={setLines} productsById={productsById} />
        </Stack>
      </Paper>

      <Group justify="flex-end">
        <Tooltip label="Requires Sales/Accounts role" disabled={canSell}>
          <span>
            <Button size="lg" disabled={!canSell || !canSubmit} loading={createSalesOrder.isPending} onClick={() => void handleSubmit()}>
              Create draft sales order
            </Button>
          </span>
        </Tooltip>
      </Group>
    </Stack>
  );
}
